import React, { useState, useContext, useCallback } from 'react';

import { ModalType, ModalData, SnackBarType, SnackBarTypeEnum, StickyHeaderType, StickyHeaderData } from 'types';

import cuid from 'cuid';

export type ContextProps = {
  activeModal?: ModalType | null;
  activeStickyHeader?: StickyHeaderType | null;
  closeModal: () => void;
  openModal: (data: ModalData) => void;
  openStickyHeader: (data: StickyHeaderData) => void;
  closeStickyHeader: () => void;
  openSnackBar: (
    text: string,
    itemMmIdIfDetails?: string,
    callback?: () => void,
    type?: SnackBarTypeEnum,
    time?: number
  ) => string;
  closeSnackBar: (id: string) => void;
  activeSnackBars: SnackBarType[];
};

export const ModalContext = React.createContext<ContextProps | null>(null);

interface Props {
  children: React.ReactNode;
}

export const useModalContext = (): ContextProps => {
  return useContext(ModalContext) as ContextProps;
};

export const ModalContextProvider: React.FC<Props> = ({ children }) => {
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [activeStickyHeader, setStickyHeader] = useState<StickyHeaderType | null>(null);
  const [activeSnackBars, setActiveSnackBars] = useState<SnackBarType[]>([]);

  const [canDisplayBrokenImageModal, setBrokenImageModalToggle] = useState(true);

  // Check localstorage for whether we have already alerted the user about their broken images
  const conditionallyOpenBrokenImagesModal = useCallback((data: ModalData) => {
    const dateLastDisplayed = localStorage.getItem('brokenImagesDialogDisplayed');
    if (!dateLastDisplayed) {
      localStorage.setItem('brokenImagesDialogDisplayed', new Date().toISOString());
      setActiveModal({
        isVisible: true,
        data
      });
      setBrokenImageModalToggle(false);
      return;
    }

    const previouslyDisplayed = new Date(dateLastDisplayed);
    const now = new Date();

    // is the difference between these two times less than 7 days?
    if (now.valueOf() - previouslyDisplayed.valueOf() < 7 * 24 * 60 * 60 * 1000) {
      setBrokenImageModalToggle(false);
    } else {
      localStorage.setItem('brokenImagesDialogDisplayed', new Date().toISOString());
      setActiveModal({
        isVisible: true,
        data
      });
    }
  }, []);

  const conditionallyOpenDoorDashModal = useCallback((data: ModalData) => {
    const alreadyDisplayed = sessionStorage.getItem('doordashDialogDisplayed');
    if (alreadyDisplayed) return;
    sessionStorage.setItem('doordashDialogDisplayed', 'true');
    setActiveModal({
      isVisible: true,
      data
    });
  }, []);

  const conditionallyOpenOptionsAvailabilityUnsupportedModal = useCallback((data: ModalData) => {
    const alreadyDisplayed = sessionStorage.getItem('optionsAvailabilityUnsupportedAppDialogDisplayed');
    if (alreadyDisplayed) return;
    sessionStorage.setItem('optionsAvailabilityUnsupportedAppDialogDisplayed', 'true');
    setActiveModal({
      isVisible: true,
      data
    });
  }, []);

  const closeModal = useCallback((): void => {
    setActiveModal({
      ...activeModal,
      isVisible: false
    });

    setTimeout(() => {
      setActiveModal(null);
    }, 300);
  }, [activeModal]);

  const closeStickyHeader = useCallback((): void => {
    setStickyHeader({ isVisible: false });
    setTimeout(() => {
      setStickyHeader(null);
    }, 300);
  }, [setStickyHeader]);

  const closeSnackBar = useCallback((id: string): void => {
    setActiveSnackBars((prev) => prev.filter((snackBar) => snackBar.id !== id));
  }, []);

  const openSnackBar = useCallback(
    (
      text: string,
      itemMmIdIfDetails?: string,
      callback?: () => void,
      type = SnackBarTypeEnum.SUCCESS,
      time = 4000
    ): string => {
      const id = cuid();
      const newSnackBar: SnackBarType = {
        id,
        itemMmIdIfDetails,
        text,
        type,
        time,
        callback
      };
      setActiveSnackBars((prev) => [...prev, newSnackBar]);
      return id;
    },
    []
  );

  const openModal = useCallback(
    (data: ModalData): void => {
      // Only alert the user about broken images once
      if (data.type === 'BROKEN_IMAGES') {
        if (!canDisplayBrokenImageModal) {
          return;
        }
        conditionallyOpenBrokenImagesModal(data);
        return;
      }
      if (data.type === 'DOORDASH_WARNING') {
        conditionallyOpenDoorDashModal(data);
        return;
      }
      if (data.type === 'OPTIONS_AVAILABILITY_UNSUPPORTED_APP_WARNING') {
        conditionallyOpenOptionsAvailabilityUnsupportedModal(data);
        return;
      }

      setActiveModal({
        isVisible: true,
        data
      });
    },
    [
      canDisplayBrokenImageModal,
      conditionallyOpenBrokenImagesModal,
      conditionallyOpenDoorDashModal,
      conditionallyOpenOptionsAvailabilityUnsupportedModal
    ]
  );

  const openStickyHeader = useCallback(
    (data: StickyHeaderData): void => {
      setStickyHeader({
        isVisible: true,
        data
      });
    },
    [setStickyHeader]
  );

  return (
    <ModalContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        activeModal,
        activeStickyHeader,
        closeModal,
        openModal,
        openSnackBar,
        closeSnackBar,
        openStickyHeader,
        closeStickyHeader,
        activeSnackBars
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};
